import { useState, useRef, useEffect, useMemo } from 'react'
import { Badge } from 'components/Badge/Badge'
import { NavBar } from 'components/NavBar/NavBar'
import { ColoredDot, Flex, H1WithBack, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { DisplayCard } from 'components/DisplayCards/DisplayCard'
import {
    ActionsMenuWrapper,
    ActionsMenuButton,
    ActionsDropdown,
    ActionsDropdownItem,
    PhaseList,
    PhaseItem,
    PhaseIndicator,
    PhaseDot,
} from './SinglePlan.styled'
import { Container, ContainerHeader, ContainerContent } from 'components/Containers/Containers'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import {
    cancelOperationPlan,
    completeOperationPlan,
    getOperationPlanDashboard,
    getSingleOperationPlan,
    pauseOperationPlan,
    resumeOperationPlan,
} from 'features/operations/plansSlice'
import { phaseStatuses, planStatuses } from '../plansData'

type IPlanMenuAction = {
    label: string
    danger?: boolean
    onClick: () => void
}

export const SinglePlan = () => {
    const [showActions, setShowActions] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const { id } = useParams()
    const navigate = useNavigate()
    const { dashboard } = useSelector((state: RootState) => state.operationPlans)
    const dispatch = useDispatch<AppDispatch>()

    useEffect(() => {
        dispatch(getSingleOperationPlan(id))
        dispatch(getOperationPlanDashboard(id))
    }, [id])

    const actionItems = useMemo((): IPlanMenuAction[] => {
        const runAction = (action: typeof cancelOperationPlan) => () => {
            setShowActions(false)
            dispatch(action({ id: Number(id), single: true }))
        }

        const items: IPlanMenuAction[] = [
            {
                label: 'Duplicate',
                onClick: () => {
                    setShowActions(false)
                    navigate('duplicate')
                },
            },
            {
                label: 'Audit log',
                onClick: () => {
                    setShowActions(false)
                    navigate('audit')
                },
            },
        ]

        const cancelItem: IPlanMenuAction = {
            label: 'Cancel plan',
            danger: true,
            onClick: runAction(cancelOperationPlan),
        }

        if (dashboard?.status === 1) {
            items.push(cancelItem)
            items.push({ label: 'Edit', onClick: () => navigate('edit') })
        } else if (dashboard?.status === 2) {
            items.push(
                { label: 'Pause', onClick: runAction(pauseOperationPlan) },
                { label: 'Complete', onClick: runAction(completeOperationPlan) },
                cancelItem,
            )
        } else if (dashboard?.status === 3) {
            items.push(
                { label: 'Resume', onClick: runAction(resumeOperationPlan) },
                cancelItem,
            )
        }

        return items
    }, [dashboard])

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setShowActions(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const getPhaseExecutable = (phase) => {
        if (phase.executableType == 1) {
            return phase.taskCycleTemplate?.name
        } else if (phase.executableType == 2) {
            return phase.workflowTemplate?.name
        } else {
            return phase.processTemplate?.name
        }
    }

    return <>
        <NavBar />
        <PageWrapper>
            <Flex center gap='1rem'>
                <H1WithBack title={dashboard?.name} arrow />
                <Badge text={planStatuses.find((status) => status.value === dashboard?.status)?.label} green={dashboard?.status === 2 || dashboard?.status === 4} red={dashboard?.status === 5} orange={dashboard?.status === 1 || dashboard?.status === 3} />
                <BodyText grey>{dashboard?.code}</BodyText>
                <ActionsMenuWrapper ref={menuRef}>
                    <ActionsMenuButton onClick={() => setShowActions((p) => !p)}>
                        <span />
                        <span />
                        <span />
                    </ActionsMenuButton>
                    <ActionsDropdown show={showActions}>
                        {actionItems.map((item) => (
                            <ActionsDropdownItem key={item.label} $danger={item.danger} onClick={item.onClick}>
                                <OtherText>{item.label}</OtherText>
                            </ActionsDropdownItem>
                        ))}
                    </ActionsDropdown>
                </ActionsMenuWrapper>
            </Flex>
            <Flex gap='1rem' style={{ margin: '2rem 0' }}>
                <DisplayCard value={dashboard?.tasksGeneratedCount} label='Tasks generated' borderColor='#ff9900' />
                <DisplayCard value={dashboard?.tasksCompletedCount} label='Completed' borderColor='#ff9900' />
                <DisplayCard value={dashboard?.queueDepth} label='Queue depth' borderColor='#ff9900' />
                <DisplayCard value='N / A' label='Drift' borderColor='#ff9900' />
                <DisplayCard value={dashboard?.workersActiveCount} label='Workers active' borderColor='#ff9900' />
                <DisplayCard value='N / A' label='Trigger fires' borderColor='#ff9900' />
            </Flex>
            <Flex gap='1rem' width='100%' >
                <Flex column gap='1rem' width='70%'>
                    <Container>
                        <ContainerHeader>
                            <Flex between center width='100%'>
                                <TitleText weight='bold'>Phases</TitleText>
                            </Flex>
                        </ContainerHeader>
                        <ContainerContent>
                            <PhaseList>
                                {dashboard?.phases?.map((phase) => (
                                    <PhaseItem key={phase.id}>
                                        <PhaseIndicator>
                                            <PhaseDot />
                                        </PhaseIndicator>
                                        <Flex column gap='0.25rem' width='100%'>
                                            <Flex center gap='0.5rem'>
                                                <Flex column gap='0.25rem' mr='16' style={{ width: '50%' }} >
                                                    <TitleText weight='bold'>{phase.name}</TitleText>
                                                    <TitleText>{getPhaseExecutable(phase)}</TitleText>
                                                    <OtherText grey>{phase.executableType == 1 ? 'Task Cycle' : phase.executableType == 2 ? 'Workflow' : 'Process'}</OtherText>
                                                </Flex>


                                                <Flex width='100%' justend >
                                                    <Badge text={phaseStatuses.find((status) => status.value === phase.status)?.label} green={phase.status === 2 || phase.status === 3} red={phase.status === 4} orange={phase.status === 1} />
                                                </Flex>

                                            </Flex>
                                        </Flex>
                                    </PhaseItem>
                                ))}
                            </PhaseList>
                        </ContainerContent>
                    </Container>

                    <Container>
                        <ContainerHeader>
                            <TitleText weight='bold'>Rolling Horizon</TitleText>
                        </ContainerHeader>
                        <ContainerContent>
                            <Flex width='100%' >
                                <Flex column gap='0.2rem' width='100%' >
                                    <OtherText grey>TYPE</OtherText>
                                    <TitleText>{dashboard?.horizon?.horizonType == 1 ? 'Next Hours' : 'Next Shift'} </TitleText>
                                </Flex>
                                <Flex column gap='0.2rem' width='100%' >
                                    <OtherText grey>REPLISH THRESHOLD</OtherText>
                                    <TitleText>{dashboard?.horizon?.replenishThreshold ?? 'N / A'} </TitleText>
                                </Flex>
                                <Flex column gap='0.2rem' width='100%' >
                                    <OtherText grey>SCHEDULER</OtherText>
                                    <TitleText>{dashboard?.horizon?.schedulerRunFreqMin ? `${dashboard?.horizon?.schedulerRunFreqMin} Min` : 'N / A'} </TitleText>
                                </Flex>
                                <Flex column gap='0.2rem' width='100%' >
                                    <OtherText grey>QUEUE HEALTH</OtherText>
                                    <TitleText>{dashboard?.horizon?.queueHealth == 1 ? 'On Track' : 'Behind'} </TitleText>
                                </Flex>
                            </Flex>
                        </ContainerContent>
                    </Container>
                </Flex>
                <Flex width='30%' column gap='1rem' >
                    <Container>
                        <ContainerHeader>
                            <TitleText weight='bold'>Alerts · last 24h</TitleText>
                        </ContainerHeader>
                        <ContainerContent>
                            <Flex column gap='0.75rem'>
                                <Flex column gap='0.25rem'>
                                    <BodyText style={{ margin: 0 }}>Phase delay — Haulage</BodyText>
                                    <OtherText grey>Queue behind by 12 min · 2h ago</OtherText>
                                </Flex>
                                <Flex column gap='0.25rem'>
                                    <BodyText style={{ margin: 0 }}>Replenish threshold reached</BodyText>
                                    <OtherText grey>Rolling horizon refreshed · 5h ago</OtherText>
                                </Flex>
                            </Flex>
                        </ContainerContent>
                    </Container>

                    <Container>
                        <ContainerHeader>
                            <TitleText weight='bold'>Queue health by department
                            </TitleText>
                        </ContainerHeader>
                        <ContainerContent>
                            <Flex column gap='0.75rem'>
                                {[
                                    { dept: 'Mining Ops', status: 'On Track', behind: 0 },
                                    { dept: 'Haulage', status: 'Behind', behind: 3 },
                                    { dept: 'Processing', status: 'On Track', behind: 0 },
                                    { dept: 'Maintenance', status: 'Behind', behind: 1 },
                                ].map((row) => (
                                    <Flex key={row.dept} between center width='100%'>
                                        <BodyText style={{ margin: 0 }}>{row.dept}</BodyText>
                                        <Flex center gap='0.5rem'>
                                            <ColoredDot color={row.status === 'On Track' ? '#24D07B' : '#FF9900'} />
                                            <OtherText grey style={{ margin: 0 }}>
                                                {row.status}{row.behind > 0 ? ` · ${row.behind} tasks` : ''}
                                            </OtherText>
                                        </Flex>
                                    </Flex>
                                ))}
                            </Flex>
                        </ContainerContent>
                    </Container>
                </Flex>

            </Flex>
        </PageWrapper>
    </>
}